import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BxCardComponent, injectBxIcons, markerPin01Icon } from '@beyonnex/bx-angular-ui';
import { TranslateService } from '@ngx-translate/core';
import { ROUTES } from '../../../app.models';
import { GEO_LOCATION_SUBJECT, type GeoLocationModalData } from '../../../components/modals/geo-location-modal/geo-location-modal.models';
import { GeoLocationModalService } from '../../../components/modals/geo-location-modal/geo-location-modal.service';
import { formatDate, formatDateTime } from '../../../models/core.models';
import { CARDINALITY, type Cardinality } from '../../../models/device.models';
import { formatAddress, RoomType } from '../../../models/foundation.models';
import { IPulseGasMeterInfo, ThingType, type ValveIssueColor } from '../../../models/thing.models';
import { CommonTranslateService } from '../../../services/common-translate/common-translate.service';
import { isNullOrUndefined } from '../../../utils/utils';
import { DeviceDetailsStore } from '../device-details.store';
import { AttributesPanelComponent } from './attributes-panel/attributes-panel.component';
import { AttributeLink, AttributesPanelData } from './attributes-panel/attributes-panel.models';
import { DeviceTagsComponent } from './device-tags/device-tags.component';

@Component({
  selector: 'bxi-device-attributes',
  imports: [NgOptimizedImage, BxCardComponent, AttributesPanelComponent, DeviceTagsComponent],
  templateUrl: './device-attributes.component.html',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceAttributesComponent {
  private readonly translate = inject(TranslateService);
  private readonly commonTranslate = inject(CommonTranslateService);
  private readonly store = inject(DeviceDetailsStore);
  private readonly geoLocationModal = inject(GeoLocationModalService);

  protected readonly device = toSignal(this.store.device$);
  protected readonly gatewayLink = toSignal(this.store.gatewayLink$);
  protected readonly room = toSignal(this.store.room$);

  protected readonly imgRootPath = '/assets/images/devices/';

  private readonly deviceType = computed(() => this.device()?.attributes?.type);
  // Reset per device, so one type's missing image does not stick to the next device shown on this page.
  protected readonly imageFailed = linkedSignal({ source: this.deviceType, computation: () => false });
  protected readonly imagePath = computed(() => {
    const type = this.deviceType();
    return type && !this.imageFailed() ? `${this.imgRootPath}${type}.svg` : `${this.imgRootPath}unknown.png`;
  });

  protected readonly srt = toSignal(this.store.srtDevice$);
  protected readonly ts = toSignal(this.store.tsDevice$);
  protected readonly gateway = toSignal(this.store.lorawanGateway$);
  protected readonly submetering = toSignal(this.store.submeteringDevice$);
  protected readonly wmbus = toSignal(this.store.wirelessMBusDevice$);
  protected readonly pulseGasMeter = toSignal(this.store.pulseGasMeter$);

  private readonly building = toSignal(this.store.building$);
  private readonly address = computed(() => formatAddress(this.building()?.getAddress()));
  private readonly geoLocation = computed(() => this.building()?.getResolvedGeoLocation());
  private readonly canChangeGeoLocation = toSignal(this.store.canChangeBuildingGeoLocation$, { initialValue: false });

  protected readonly basicInformation: Signal<AttributesPanelData> = computed(() => {
    const device = this.device();
    const submetering = this.submetering();
    const srt = this.srt();
    const attributes: AttributesPanelData['attributes'] = [
      {
        label: this.translate.instant('attributes.type'),
        value: this.commonTranslate.translateThingTypeAbbreviation(device?.attributes?.type as ThingType),
      },
      { label: this.translate.instant('attributes.serial'), value: device?.attributes?.serial },
      { label: this.translate.instant('attributes.manufacturer'), value: device?.attributes?.manufacturer },
    ];

    const wmbus = this.wmbus();
    if (wmbus) {
      attributes.push({ label: this.translate.instant('attributes.dinId'), value: wmbus.getDinId() ?? '-' });
    }

    if (this.ts()) {
      attributes.push({ label: 'DevEUI', value: this.ts()?.attributes?.devEUI });
      attributes.push({ label: this.translate.instant('attributes.version'), value: this.ts()?.attributes?.model });
    }

    const pulseGasMeter = this.pulseGasMeter();
    if (pulseGasMeter) {
      attributes.push({ label: 'DevEUI', value: pulseGasMeter.attributes?.devEUI });
      attributes.push({ label: this.translate.instant('attributes.productKey'), value: pulseGasMeter.attributes?.productKey });
      attributes.push({ label: this.translate.instant('attributes.channel'), value: pulseGasMeter.attributes?.channel });
    }

    if (srt) {
      attributes.push({ label: 'DevEUI', value: srt?.attributes?.devEUI });
      attributes.push({
        label: this.translate.instant('attributes.hardware.technicalName'),
        value: srt?.attributes?.hardware?.technicalName,
      });
      const firmwareVersion = srt.getFirmwareVersion();
      if (firmwareVersion) {
        attributes.push({
          label: this.translate.instant(firmwareVersion.labelKey),
          value: firmwareVersion.value,
        });
      }
    }

    if (this.gateway()) {
      attributes.push({ label: 'DevEUI', value: this.gateway()?.attributes?.devEUI });
      attributes.push({ label: this.translate.instant('attributes.version'), value: this.gateway()?.attributes?.['gateway-model'] });
    }

    if (submetering) {
      attributes.push({
        label: this.translate.instant('features.grid.properties.gateway'),
        value: submetering.features?.grid?.properties?.gateway,
        ...this.gatewayLink(),
      });
      attributes.push({ label: this.translate.instant('attributes.version'), value: submetering.attributes?.version });
      attributes.push({
        label: this.translate.instant('attributes.location.isAdvancedRegistrationMeter'),
        value: submetering.attributes?.location?.isAdvancedRegistrationMeter ?? '-',
      });
    }

    return {
      title: this.translate.instant('thing.attributes.basicInformation'),
      attributes,
    };
  });

  protected readonly location: Signal<AttributesPanelData> = computed(() => {
    const device = this.device();
    const title = this.translate.instant('thing.attributes.location');
    const geoLocation = this.geoLocation();
    const addressRow = {
      label: this.translate.instant('system.address'),
      value: this.address() || '-',
      wrap: true,
    };
    // A building without coordinates is told apart from a device with no building at all.
    const geoLocationRow: AttributesPanelData['attributes'][number] = geoLocation
      ? { label: this.translate.instant('system.geoLocation'), link: this.viewGeoLocationLink() }
      : {
          label: this.translate.instant('system.geoLocation'),
          value: this.building() ? this.translate.instant('geoLocation.notDetermined') : '-',
        };
    // Not mounted in a billing unit or room: only the building address says where it is.
    if (this.pulseGasMeter()) {
      return { title, attributes: [addressRow, geoLocationRow] };
    }

    const mountedOn = device?.attributes?.location?.mountedOn;
    const unmountedOn = device?.attributes?.location?.unmountedOn;
    const room = this.room();

    const attributes: AttributesPanelData['attributes'] = [
      {
        label: this.translate.instant('attributes.location.billingUnitReference'),
        value: device?.attributes?.location?.billingUnitReference ?? '-',
        href: device?.attributes?.location?.billingUnitReference
          ? `/${ROUTES.DEVICES}?gridFilters=${JSON.stringify({
              'attributes.location.billingUnitReference': {
                filterType: 'text',
                type: 'equals',
                filter: device?.attributes?.location?.billingUnitReference,
              },
            })}`
          : undefined,
      },
      {
        label: this.translate.instant('attributes.location.roomGroup.number'),
        value: device?.attributes?.location?.roomGroup?.number ?? '-',
        href: device?.attributes?.location?.billingUnitReference
          ? `/${ROUTES.DEVICES}?gridFilters=${JSON.stringify({
              'attributes.location.billingUnitReference': {
                filterType: 'text',
                type: 'equals',
                filter: device?.attributes?.location?.billingUnitReference,
              },
              'attributes.location.roomGroup.number': {
                filterType: 'text',
                type: 'equals',
                filter: device?.attributes?.location?.roomGroup?.number,
              },
            })}`
          : undefined,
      },
      {
        label: this.translate.instant('attributes.location.room.floorLevel'),
        value: device?.attributes?.location?.room?.floorLevel ?? '-',
        href: device?.attributes?.location?.billingUnitReference
          ? `/${ROUTES.DEVICES}?gridFilters=${JSON.stringify({
              'attributes.location.billingUnitReference': {
                filterType: 'text',
                type: 'equals',
                filter: device?.attributes?.location?.billingUnitReference,
              },
              'attributes.location.room.floorLevel': {
                filterType: 'text',
                type: 'equals',
                filter: device?.attributes?.location?.room?.floorLevel,
              },
            })}`
          : undefined,
      },
      {
        label: this.translate.instant('attributes.location.room.type'),
        value: this.commonTranslate.translateRoomType(this.device()?.attributes?.location?.room?.type as RoomType) || '-',
      },
      {
        label: this.translate.instant('attributes.number'),
        value: room?.attributes?.number || '-',
      },
      {
        label: this.translate.instant('attributes.description'),
        value: room?.attributes?.description || '-',
      },
      {
        label: this.translate.instant('attributes.location.mountedOn'),
        value: (mountedOn && formatDate(mountedOn)) ?? '-',
      },
      {
        label: this.translate.instant('attributes.location.unmountedOn'),
        value: (unmountedOn && formatDate(unmountedOn)) ?? '-',
      },
      addressRow,
      geoLocationRow,
    ];

    return { title, attributes };
  });

  protected readonly settings: Signal<AttributesPanelData> = computed(() => {
    const srt = this.srt();
    if (!srt) {
      return { title: '', attributes: [] };
    }

    const lastReboot = srt?.features?.thermostat?.properties?.events?.reboot?.date;
    const lastCalibration = srt?.features?.thermostat?.properties?.events?.calibrationResult?.date;

    return {
      title: this.translate.instant('thing.attributes.settings'),
      attributes: [
        {
          label: this.translate.instant('features.thermostat.properties.events.reboot.date'),
          value: lastReboot && formatDate(lastReboot),
        },
        {
          label: this.translate.instant('features.thermostat.properties.events.calibrationResult.date'),
          value: lastCalibration && formatDate(lastCalibration),
        },
        {
          label: this.translate.instant('features.thermostat.properties.configuration.motorLimitOffset'),
          value: srt?.features?.thermostat?.properties?.configuration?.motorLimitOffset,
        },
        {
          label: this.translate.instant('features.temperatureControl.properties.status.temperatureOffsetStatus.value'),
          value: srt?.features?.temperatureControl?.properties?.status?.temperatureOffsetStatus?.value,
        },
      ].filter((d) => !isNullOrUndefined(d?.value)),
    };
  });

  protected readonly installationPoint: Signal<AttributesPanelData> = computed(() => {
    const srt = this.srt();
    if (!srt) {
      return { title: '', attributes: [] };
    }

    const { color, reason, updatedAt } = srt.getValveIssueIndicator() ?? {};
    const cardinality = color && VALVE_ISSUE_CARDINALITY[color];
    const colorLabel = this.translate.instant('features.monitoring.properties.status.valveIssueIndicator.color');
    const tooltip =
      updatedAt &&
      `${this.translate.instant('features.monitoring.properties.status.valveIssueIndicator.updatedAt')}: ${formatDateTime(updatedAt)}`;
    const reasonLabel = this.translate.instant('features.monitoring.properties.status.valveIssueIndicator.reason');
    const lines = this.commonTranslate.translateValveIssueReason(color, reason);

    return {
      title: this.translate.instant('thing.attributes.installationPoint'),
      attributes: [
        cardinality ? { label: colorLabel, badge: { cardinality, tooltip } } : { label: colorLabel, value: '-' },
        lines.length ? { label: reasonLabel, lines } : { label: reasonLabel, value: '-' },
      ],
    };
  });

  protected readonly meterInfo: Signal<AttributesPanelData> = computed(() => {
    const pulseGasMeter = this.pulseGasMeter();
    if (!pulseGasMeter) {
      return { title: '', attributes: [] };
    }

    const meterInfo: IPulseGasMeterInfo = pulseGasMeter.getMeterInfo() ?? {};
    const unit = pulseGasMeter.getMeterUnit();
    const withUnit = (value: unknown): string => (unit ? this.commonTranslate.formatUnit(unit, value) : String(value));
    const { value, measuredAt } = meterInfo.installationMeasurement ?? {};
    const installationReading = isNullOrUndefined(value) ? '-' : this.commonTranslate.formatValueAt(withUnit(value), measuredAt);
    const { pulseCoefficient } = meterInfo;
    const coefficient = isNullOrUndefined(pulseCoefficient) ? '-' : this.commonTranslate.formatPerImpulse(withUnit(pulseCoefficient));

    return {
      title: this.translate.instant('thing.attributes.meterInfo'),
      attributes: [
        { label: this.translate.instant('attributes.meterInfo.serial'), value: meterInfo.serial ?? '-' },
        { label: this.translate.instant('attributes.meterInfo.manufacturer'), value: meterInfo.manufacturer ?? '-' },
        { label: this.translate.instant('attributes.meterInfo.pulseCoefficient'), value: coefficient },
        { label: this.translate.instant('attributes.meterInfo.installationMeasurement'), value: installationReading, wrap: true },
      ],
    };
  });

  constructor() {
    injectBxIcons([markerPin01Icon]);
  }

  private viewGeoLocationLink(): AttributeLink {
    return { label: this.translate.instant('geoLocation.view'), icon: 'marker-pin-01', action: (): void => this.openGeoLocationModal() };
  }

  private openGeoLocationModal(): void {
    const building = this.building();
    const geoLocation = this.geoLocation();
    if (!building || !geoLocation) {
      return;
    }

    const data: GeoLocationModalData = {
      thingId: building.thingId,
      subject: GEO_LOCATION_SUBJECT.BUILDING,
      address: this.address(),
      geoLocation,
      canEdit: this.canChangeGeoLocation(),
    };
    // Re-fetched on every close: the dialog closes with no result on Esc or backdrop too, and the building is not streamed.
    this.geoLocationModal.open(data).subscribe(() => this.store.refreshBuilding());
  }
}

// bxi-status-badge only renders CARDINALITY, so bxAnalytics' colours are mapped by name for display; never read the number back.
const VALVE_ISSUE_CARDINALITY: Record<ValveIssueColor, Cardinality> = {
  GREEN: CARDINALITY.OK,
  YELLOW: CARDINALITY.WARNING,
  ORANGE: CARDINALITY.ERROR,
};
